import { computed, onUnmounted, ref } from 'vue'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'

/**
 * Polls the env builder status endpoint and phrases its answer for the UI.
 *
 * This exists because the env builder's completion signal is not reliable on its own. The `*Done` / `*Failed` events
 * are `ShouldBroadcast`, queued onto the `<tenant>.ui` database queue behind a worker running with `--sleep=3`, so a
 * fully successful operation can be reported late or — if that worker is down — never. Polling gives the page an
 * authoritative answer instead of a spinner that never resolves.
 *
 * That is the same problem and the same solution as the finance sync modal (CIO-11960), and this deliberately keeps its
 * shape and its names: `readStatus` guarded by `pollInFlight`, an idempotent `startPolling`, `stopPolling` on unmount,
 * and a `catch` that keeps polling because a failed status request says nothing about the operation itself.
 *
 * One poller per page. Index.vue owns it and passes `statusHint` down to whichever pending modal is open, rather than
 * every modal running its own — the answer is per tenant, so a second poller would just fetch the same thing twice.
 */
export function useSnapshotOperationStatus () {
    // 5 s while something is running, matching the finance sync modal. The server caches for 3 s and the activity side
    // throttles heartbeats to one per 15 s, so polling faster buys nothing.
    const POLL_INTERVAL_RUNNING_MS = 5000

    // Far slower when nothing is running. The page still has to notice an operation a *colleague* starts — that is the
    // whole point of the surface — so it cannot simply stop, but at 5 s an idle page issues twelve requests a minute
    // forever for an answer that almost never changes.
    const POLL_INTERVAL_IDLE_MS = 30000

    /**
     * A client of our own, deliberately without the application's global axios interceptors.
     *
     * Those interceptors drive NProgress and the shared `isInLoadingState` counter, which exist for actions a user
     * took. A background poll is not one: routed through `window.axios` it kept the page's loading bar flickering on a
     * permanent cycle, which reads exactly like the page reloading itself. `create()` inherits the configured defaults
     * — `X-Requested-With` among them — but starts with no interceptors, which is precisely the difference wanted.
     */
    const poller = window.axios.create()

    const status = ref(null)
    const pollTimer = ref(null)
    const pollingWanted = ref(false)
    const pollInFlight = ref(false)

    /**
     * Whether the poll has had a usable answer yet. Set only by fetchStatus, the one place that sees an answer land.
     * Internal: callers hear about it through onFirstAnswer, which also covers a caller subscribing after the fact.
     */
    const statusAnswered = ref(false)
    const firstAnswerListeners = []

    /**
     * A start that has not answered yet. A Symbol, so it can equal no run id.
     */
    const PENDING_START = Symbol('pending start')

    /**
     * The run the caller is waiting on: a run id, PENDING_START while a start has not answered yet, or null for none.
     */
    const awaitedRunId = ref(null)

    const asRunId = (value) => (typeof value === 'string' && value !== '' ? value : null)

    /**
     * True only when Temporal positively reported a running operation.
     *
     * `unknown` deliberately does not count. It means Temporal could not be reached, and rendering that as "something
     * is running" would disable the whole page every time the status endpoint hiccups.
     */
    const isRunning = computed(() => status.value?.state === 'running')

    /**
     * True when the status is not usable either way. Kept separate from isRunning so the copy can say which it is.
     */
    const isUnknown = computed(() => status.value?.state === 'unknown')

    const operation = computed(() => status.value?.operation?.type ?? null)

    /**
     * The Temporal run id of the execution this answer describes, running or finished.
     *
     * Every operation for a tenant shares one workflow ID, so this is the only field that tells one execution from the
     * next. Null for `idle` and `unknown`, and for a status from a backend that does not send it.
     */
    const runId = computed(() => asRunId(status.value?.operation?.run_id))

    /**
     * Whether the current answer is about the awaited run — tri-state, and the third state matters.
     *
     * `true` / `false` when both sides are known, and `false` for any answer carrying a run id while a start is
     * pending: until the start names its run, no id can be trusted to be it. `null` when either side is missing, which
     * means *cannot tell*, never *not this run*: the caller falls back rather than waits for an id that is not coming.
     */
    const describesAwaitedRun = computed(() => {
        if (awaitedRunId.value === null || runId.value === null) {
            return null
        }

        return runId.value === awaitedRunId.value
    })

    /**
     * Wait on the run a start is about to create, ahead of its request — until awaitRun names it.
     */
    const awaitStart = () => {
        awaitedRunId.value = PENDING_START
    }

    /**
     * Wait on this run. Anything but a non-empty string — an old backend's answer, a refused start — waits on none.
     */
    const awaitRun = (id) => {
        awaitedRunId.value = asRunId(id)
    }

    /**
     * Call back once, synchronously, when the first answer lands — before any watcher or render sees it.
     */
    const onFirstAnswer = (callback) => {
        if (statusAnswered.value) {
            callback()

            return
        }

        firstAnswerListeners.push(callback)
    }

    const startedByUserName = computed(() => status.value?.operation?.started_by_user_name ?? null)

    /**
     * Who started it, as an id.
     *
     * The caller needs this to decide whether *this* user can be shown the operation's pending modal again. Reopening
     * it for someone else's operation would hang: the `*Done` events are broadcast on the starting user's private
     * channel, and the comparison result is cached under their id, so neither would ever reach this browser.
     */
    const startedByUserId = computed(() => status.value?.operation?.started_by_user_id ?? null)

    /**
     * The snapshot the operation targets, or null for a create — which has no file to name until it produces one.
     *
     * Recovered from the workflow memo, so it survives a reload. Without it, a compare resolved by the poll rather than
     * by the broadcast could only hand the results modal a blank label, because the filename otherwise lives nowhere
     * but the click that started the operation.
     */
    const snapshot = computed(() => status.value?.operation?.snapshot ?? null)

    /**
     * The last *closed* operation and how it ended, or null when Temporal remembers none.
     *
     * This is what makes the poll authoritative for terminal state rather than merely informative. A workflow execution
     * timeout is enforced by the Temporal server and is uncatchable, so its `catch` never runs and no `*Failed` event is
     * ever broadcast for it; and the events that *are* broadcast sit on a database queue behind a worker with
     * `--sleep=3`, so they can arrive late or not at all. In both cases this is the only thing left that can tell a
     * waiting modal what happened.
     */
    const finishedOperation = computed(() => status.value?.state === 'finished' ? (status.value?.operation?.type ?? null) : null)

    const outcome = computed(() => status.value?.result?.outcome ?? null)

    /**
     * Whether the finished operation did what was asked — tri-state, and the third state matters.
     *
     * `true` / `false` when known; `null` when the workflow completed but its result could not be read. It is a
     * separate question from `outcome`, because all three workflows catch their own failures and *return* a response
     * carrying `success: false` rather than rethrowing — so a failed operation closes as `completed`, and reading the
     * outcome alone would report success for a failed apply.
     */
    const outcomeSucceeded = computed(() => {
        const value = status.value?.result?.succeeded

        return value === true || value === false ? value : null
    })

    const startedAt = computed(() => {
        const raw = status.value?.operation?.started_at

        if (typeof raw !== 'string') {
            return null
        }

        const parsed = new Date(raw)

        return Number.isNaN(parsed.getTime()) ? null : parsed
    })

    /**
     * A short line naming the operation and who started it, for the non-modal surface.
     */
    const runningSummary = computed(() => {
        if (isRunning.value === false) {
            return null
        }

        const name = startedByUserName.value

        return {
            operation: __(`tools.testing.snapshots.status.operation.${operation.value}`),
            snapshot: snapshot.value,
            startedBy: name === null
                // The artisan entry points pass no user, so there is genuinely nobody to name.
                ? __('tools.testing.snapshots.status.started_by_unknown')
                : __('tools.testing.snapshots.status.started_by', { user: name }),
            startedAt: startedAt.value === null ? null : startedAt.value.toLocaleString(),
        }
    })

    /**
     * How a finished operation ended, in one sentence. The single place that decision is made.
     *
     * Every surface that phrases an outcome — the finished notice on the page, and the failure text the pending modals
     * put into their toast — reads it from here. It used to be written out twice, and the two copies shared the same
     * hole: an operation that failed but rolled back cleanly closes in Temporal as `completed` while returning
     * `success: false`, so keying on the execution status alone answered "The operation completed." for a failure —
     * which the modal path then interpolated into a red "Failed to …. Error: The operation completed." toast.
     */
    const describeOutcome = (succeeded) => {
        if (succeeded === true) {
            return __('tools.testing.snapshots.status.finished_ok')
        }

        // Finished, but the workflow's own verdict could not be read. Never phrased as either outcome.
        if (succeeded === null) {
            return __('tools.testing.snapshots.status.outcome_unreadable')
        }

        // The workflow's own reason, when it gave one: the very text its `*Failed` event carries. A refused create or
        // compare closes within about a second, so the poll right after the start usually resolves the modal before
        // that event has left its queue — and once the modal is gone, so is the listener. Without this the user only
        // ever saw "reported a failure" for a refusal that had named its limits (CIO-14239).
        const reason = status.value?.result?.reason

        if (typeof reason === 'string' && reason !== '') {
            return reason
        }

        // The workflows catch their own failures and return `success: false` rather than rethrowing, so a failed
        // operation closes as `completed`. The execution status has nothing to say about that one; every other status
        // (failed, timed out, terminated, …) describes the failure by itself.
        return outcome.value === 'completed'
            ? __('tools.testing.snapshots.status.outcome_reported_failure')
            : __(`tools.testing.snapshots.status.outcome.${outcome.value}`)
    }

    /**
     * The counterpart to runningSummary for an operation that has finished.
     *
     * Present for as long as Temporal remembers the last execution, which is the whole namespace retention period — so
     * a caller must decide for itself whether a given finished operation is still worth showing. That decision is made
     * on the raw `closed_at` the status carries, compared as an identity rather than as a point in time.
     */
    const finishedSummary = computed(() => {
        if (status.value?.state !== 'finished' || finishedOperation.value === null) {
            return null
        }

        const succeeded = outcomeSucceeded.value

        return {
            operation: __(`tools.testing.snapshots.status.operation.${finishedOperation.value}`),
            snapshot: snapshot.value,
            succeeded,
            startedByUserId: startedByUserId.value,
            message: describeOutcome(succeeded),
        }
    })

    /**
     * The line rendered under the spinner, and on the surface.
     *
     * Phrased as what is happening now, never as a percentage or an ETA: nothing checkpoints, so a retried activity
     * restarts from the beginning and there is no total to divide by.
     *
     * Degrades upwards, never to an empty line. Heartbeats are throttled to one per 15 s and the short activities do
     * not heartbeat at all, so "no progress detail" is the normal state for a healthy operation, not a fault.
     */
    const statusHint = computed(() => {
        if (isRunning.value === false) {
            return null
        }

        const operationLabel = __(`tools.testing.snapshots.status.operation.${operation.value}`)
        const progress = status.value?.pending_activity?.progress

        if (progress === null || progress === undefined) {
            return operationLabel
        }

        const phase = __(`tools.testing.snapshots.status.phase.${progress.phase}`)
        const subject = describeSubject(progress)

        return subject === null
            ? phase
            : `${phase} — ${subject}`
    })

    /**
     * The section and model a phase is working on, as a display string.
     *
     * The model class arrives as a fully qualified name and is reduced to its basename here. That basename *is* the
     * display name the rest of this tool uses — ModelDiscoveryService's NamedModel does exactly the same thing — and
     * going through that service instead would not help: it is per section, top-level models only, and only built for
     * two of the five sections, so a child model or a customers-section model would simply be missing.
     */
    const describeSubject = (progress) => {
        const model = typeof progress.model_class === 'string'
            ? progress.model_class.split('\\').pop()
            : null

        const section = typeof progress.section === 'string' ? progress.section : null

        if (model !== null && section !== null) {
            return `${section} / ${model}`
        }

        return model ?? section
    }

    const fetchStatus = async () => {
        if (pollInFlight.value) {
            return
        }

        pollInFlight.value = true

        try {
            const { data } = await poller.get(route('tools.snapshot.status'))

            status.value = data

            // `unknown` means Temporal could not be reached, so this answer carries no `closed_at` and knows of no
            // previous outcome — it cannot be told apart from "the tenant has never run anything". Treating it as the
            // first answer would baseline the page at null and let the next one, reporting a days-old run, announce it
            // as news. `idle` is a real answer and must still count: it legitimately means there is no known previous
            // outcome.
            if (statusAnswered.value === false && isUnknown.value === false) {
                statusAnswered.value = true
                firstAnswerListeners.splice(0).forEach((callback) => callback())
            }
        } catch (error) {
            // A failing status request tells us nothing about the operation itself, so keep the last known answer and
            // keep polling. This must never fail the page, the modal or the operation.
            console.error(error)
        } finally {
            pollInFlight.value = false
        }
    }

    /**
     * Scheduled with setTimeout rather than setInterval so each tick can pick its own delay.
     *
     * The rate has to follow the state: a running operation wants 5 s so the phase hint keeps up, an idle page wants
     * to stay quiet. Each call replaces the pending timer, so there is only ever one chain: a poll that lands after
     * readStatus re-timed it takes the chain over rather than starting a second one.
     */
    const scheduleNext = () => {
        if (pollingWanted.value === false) {
            return
        }

        clearTimeout(pollTimer.value)
        pollTimer.value = setTimeout(
            async () => {
                await fetchStatus()
                scheduleNext()
            },
            isRunning.value ? POLL_INTERVAL_RUNNING_MS : POLL_INTERVAL_IDLE_MS
        )
    }

    const stopPolling = () => {
        pollingWanted.value = false

        if (pollTimer.value !== null) {
            clearTimeout(pollTimer.value)
            pollTimer.value = null
        }
    }

    const startPolling = () => {
        if (pollingWanted.value) {
            return
        }

        pollingWanted.value = true
        fetchStatus().then(scheduleNext)
    }

    /**
     * Read now, and re-time the next poll around this answer.
     *
     * Called once a start has answered. The start endpoint drops the server's cached status first, so this read
     * normally describes the new run and steps the cadence up to the running rate. A poll already in flight answers in
     * its place; a describe that raced the drop, or an older backend, serves the pre-start answer and idles one poll.
     */
    const readStatus = async () => {
        await fetchStatus()

        scheduleNext()
    }

    onUnmounted(stopPolling)

    return {
        status,
        isRunning,
        isUnknown,
        onFirstAnswer,
        operation,
        runId,
        awaitStart,
        awaitRun,
        describesAwaitedRun,
        startedAt,
        startedByUserName,
        startedByUserId,
        snapshot,
        finishedOperation,
        outcome,
        outcomeSucceeded,
        runningSummary,
        finishedSummary,
        statusHint,
        readStatus,
        startPolling,
        stopPolling,
    }
}
